// This was generated during the migration to @kiwicom/eslint-config
// PLEASE REMOVE THE GLOBAL ESLINT DISABLE BELOW
// AND FIX ERRORS ACCORDINGLY.
/* eslint-disable new-cap, babel/new-cap, no-underscore-dangle, multiline-comment-style, no-negated-condition, no-param-reassign */
import { List, Record, Map, Set } from "immutable"
import moment from "moment"
import * as R from "ramda"

import { get } from "../utils/get"
// eslint-disable-next-line import/no-restricted-paths
import airlineRestrictions from "../../../data/airlineRestrictions.json"
import Trip from "./Trip"
import JourneyProvider from "./JourneyProvider"
import type Flight from "./Flight"
import Stop from "./Stop/Stop"
import type { SupportedJourneyHiddenTypes } from "../services/journey/types"
import type { PART_TYPE } from "../consts/booking"
import { PART_TYPES } from "../consts/booking"

const JourneyRecord = Record({
  bookingToken: null,
  hasHoldBagsAllowed: true, // cannot be used in MMB
  sources: Set(),
  trips: List(),
  transfers: List(),
  flights: List(),
  id: null,
  // prices used only on MMB SHOT
  prices: Map(),
  quality: null,
  debugColor: "",
  debugString: "",
  _original: null, // original response, only for debugging purposes
  type: "oneWay",
  providers: List(),
  disabledBagsInMmb: false,
  hiddenFlightsType: null,
  hasHiddenCity: false,
  visaCheckRequiredFor: [], // shot
  holdBagsDisabled: null, // shot
  countryFrom: "",
  countryTo: "",
})

export const getMidnight = (date: moment.Moment) => moment.utc(date).startOf("day")

export type DifferentAirportsData = {
  arrival: string
  departure: string
  city: string
  dataIdToHighlight: string
  lastPart: boolean
  country?: string
}

export type DifferentAirports = Map<
  number,
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
  {
    [key: string]: DifferentAirportsData
  }
>
// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Layover = Map<string, any>
export type Layovers = List<Layover>
export type ProvidersListType = List<JourneyProvider>

export default class Journey extends JourneyRecord {
  _flights: List<Flight> | null | undefined = null

  _flightsId: string | null | undefined = null

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  getTimeUntil(unit: string = "seconds") {
    return this.getDeparture().getIn(["when", "utc"]).diff(moment.utc(), unit)
  }

  /**
   Computes id from flight ids
   Should replace static getId in the future
   */
  getId(): string {
    // FIXME: JS doesn't have a concept of public and private, so don't use dangling underscores if possible

    if (!this._flightsId) {
      // FIXME: JS doesn't have a concept of public and private, so don't use dangling underscores if possible

      this._flightsId = this.getFlightsIds().join("-")
    }
    // FIXME: JS doesn't have a concept of public and private, so don't use dangling underscores if possible

    return this._flightsId
  }

  hasThrowawayTicket(): boolean {
    // @ts-expect-error: Flow to TS migration.
    return this.getFlights().some((flight) => flight.isThrowawayTicket)
  }

  hasHiddenCity(): boolean {
    return this.get("hasHiddenCity")
  }

  hasBus(): boolean {
    return this.getTypes().includes(PART_TYPES.BUS)
  }

  hasTrain(): boolean {
    return this.getTypes().includes(PART_TYPES.TRAIN)
  }

  hasFlight(): boolean {
    return this.getTypes().includes(PART_TYPES.FLIGHT)
  }

  getHiddenFlightsType(): SupportedJourneyHiddenTypes {
    return this.get("hiddenFlightsType")
  }

  /** Returns array of all unique combination IDs which contain a hidden city (THC). */
  getUniqueCombinationIdsWithHiddenCities(): string[] {
    if (!this.hasHiddenCity()) {
      return []
    }

    // @ts-expect-error: Flow to TS migration.
    return R.pipe(
      // @ts-expect-error: Flow to TS migration.
      R.filter((flight) => flight.isHiddenCity),
      R.pluck("combinationId"),
      R.uniq,
    )(this.getFlights().toJS())
  }

  /** Returns array of all segment IDs (hidden and non-hidden segments) with a hidden city (THC) in the combination.
   *
   * 2 or more flights are in the same combination when they share the first part
   * of the flight id. Example: "SP12345_456", "SP12345_789" are in the same combination 12345.
   */
  getSegmentIdsWithHiddenCitiesInCombination(): string[] {
    if (!this.hasHiddenCity()) {
      return []
    }

    const flights = this.getFlights()
    const combinationIdsWithHiddenCitiesSegments = this.getUniqueCombinationIdsWithHiddenCities()

    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/array-type, @typescript-eslint/no-explicit-any
    return flights.reduce<Array<any>>(
      (acc, flight) =>
        // @ts-expect-error: Flow to TS migration.
        combinationIdsWithHiddenCitiesSegments.includes(flight.get("combinationId"))
          ? // @ts-expect-error: Flow to TS migration.
            [...acc, flight.get("id")]
          : acc,
      [],
    )
  }

  getFlightsByAirline(flightCode: string): Flight[] {
    // @ts-expect-error: Flow to TS migration.
    return this.getFlights().filter((flight) => flight.getIn(["airline", "code"]) === flightCode)
  }

  /**
   * @deprecated, use native getter instead - for clarity
   */
  getPrice(): number {
    return this.getIn(["prices", "default"])
  }

  getBestPrice(): number {
    // @ts-expect-error: Flow to TS migration.
    if (this.providers.isEmpty()) {
      return this.getPrice()
    }

    // @ts-expect-error: Flow to TS migration.
    return this.providers.minBy((provider) => provider.price).price
  }

  getTypes(): Set<PART_TYPE> {
    return this.get("trips").reduce(
      (acc: Set<PART_TYPE>, trip: Trip) => acc.union(trip.getTypes()),
      Set(),
    )
  }

  getDeparture() {
    // TODO: Get first trip
    return this.getOutboundTrip().getDeparture()
  }

  getArrival() {
    // TODO: Get last trip
    if (this.isReturn()) {
      return this.getOutboundTrip().getArrival()
    }
    return this.get("trips").last().getArrival()
  }

  fareCategoryTypes(): string[] {
    return (
      this.getFlights()
        // @ts-expect-error: Flow to TS migration.
        .map((flight) => flight.fareCategory)
        .toArray()
    )
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  getReturnFlightCities(cityId: boolean = false): List<string> {
    // FIXME: a method has been detected that possibly mutates the object, consider a more
    // functional approach (Ramda might help)

    return this.get("trips")
      .reduce(
        // @ts-expect-error: Flow to TS migration.
        (cities, trip) =>
          // FIXME: a method has been detected that possibly mutates the object, consider a more
          // functional approach (Ramda might help)

          cities.push(
            trip
              .getDeparture()
              .get("where")
              .get(cityId ? "city" : "name"),
          ),
        List(),
      )
      .push(
        this.get("trips")
          .last()
          .getArrival()
          .get("where")
          .get(cityId ? "city" : "name"),
      )
  }

  /**
   * @deprecated
   */
  getOutboundTrip() {
    return this.getIn(["trips", 0])
  }

  /**
   * @deprecated
   */
  getInboundTrip() {
    return this.getIn(["trips", 1])
  }

  getFlights(): List<Flight> {
    // FIXME: JS doesn't have a concept of public and private, so don't use dangling underscores if possible

    if (!this._flights) {
      // FIXME: JS doesn't have a concept of public and private, so don't use dangling underscores if possible

      this._flights = this.get("trips").reduce(
        // @ts-expect-error: Flow to TS migration.
        (iflights, trip) => iflights.concat(trip.get("flights")),
        List(),
      )
    }
    // FIXME: JS doesn't have a concept of public and private, so don't use dangling underscores if possible

    // @ts-expect-error: Flow to TS migration.
    return this._flights
  }

  getFlightById(id: string): Flight {
    // @ts-expect-error: Flow to TS migration.
    return this.getFlights().find(({ id: _id }) => id === _id)
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFlightsIds(): List<any> {
    // @ts-expect-error: Flow to TS migration.
    return this.getFlights().map((x) => x.id)
  }

  /**
   * count flight durations + stops durations, do not count stay in destination
   */
  getDuration() {
    return this.get("trips").reduce(
      // @ts-expect-error: Flow to TS migration.
      (duration, trip) => duration.add(trip.getDuration()),
      /* $FlowFixMe This comment suppresses an error found when fixing type
       * definitions for moment. To view the error, delete this comment and run
       * Flow. */
      moment.duration(),
    )
  }

  isReturn() {
    return this.get("type") === "return"
  }

  isMulticity() {
    return this.get("type") === "multicity"
  }

  endsInFuture() {
    return this.getFlights().last().getIn(["arrival", "when", "utc"]).isAfter(moment().utc())
  }

  getLayovers(): Layovers {
    let layovers = List()
    const trips = this.get("trips")
    for (let i = 1; i < trips.count(); i += 1) {
      // Start from second flight
      const prevArrival = trips.get(i - 1).getArrival()
      const currDeparture = trips.get(i).getDeparture()
      const nightsInDestination = getMidnight(currDeparture.getIn(["when", "local"])).diff(
        getMidnight(prevArrival.getIn(["when", "local"])),
        "days",
      )

      const layover: Layover = Map({
        duration: moment.duration(
          currDeparture.getIn(["when", "utc"]).diff(prevArrival.getIn(["when", "utc"])),
        ),
        nightsInDestination,
        place: prevArrival.getIn(["where"]),
        switchToPlace:
          prevArrival.getIn(["where", "code"]) !== currDeparture.getIn(["where", "code"])
            ? currDeparture.getIn(["where"])
            : null,
      })

      // FIXME: a method has been detected that possibly mutates the object, consider a more
      // functional approach (Ramda might help)

      layovers = layovers.push(layover)
    }
    // @ts-expect-error: Flow to TS migration.
    return layovers
  }

  getCountries() {
    // @ts-expect-error: Flow to TS migration.
    return this.getFlights().reduce((agg, flight) => {
      ;["departure", "arrival"].forEach((x) => {
        const country =
          // @ts-expect-error: Flow to TS migration.
          typeof flight.getIn([x, "where", "country"]) !== "undefined"
            ? // @ts-expect-error: Flow to TS migration.
              flight.getIn([x, "where", "country"]).toLowerCase()
            : null
        // FIXME: a method has been detected that possibly mutates the object, consider a more
        // functional approach (Ramda might help)

        // @ts-expect-error: Flow to TS migration.
        if (country && !agg.includes(country)) agg = agg.push(country)
      })
      return agg
    }, List())
  }

  getAirlines(): List<string> {
    // @ts-expect-error: Flow to TS migration.
    return this.getFlights().reduce((agg, flight) => {
      // @ts-expect-error: Flow to TS migration.
      const airline = flight.getIn(["airline", "code"])
      // FIXME: a method has been detected that possibly mutates the object, consider a more
      // functional approach (Ramda might help)

      // @ts-expect-error: Flow to TS migration.
      return agg.includes(airline) ? agg : agg.push(airline)
    }, List())
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAirports(): Set<any> {
    return this.getFlights().reduce(
      (agg, flight) =>
        // @ts-expect-error: Flow to TS migration.
        agg
          // @ts-expect-error: Flow to TS migration.
          .add(flight.getIn(["arrival", "where", "code"]))
          // @ts-expect-error: Flow to TS migration.
          .add(flight.getIn(["departure", "where", "code"])),
      Set(),
    )
  }

  getTripsDifferentAirports() {
    const trips = this.get("trips")
    // Make sense only for journeys with more trips
    return trips.size > 1
      ? // @ts-expect-error: Flow to TS migration.
        trips.reduce((acc, trip, index) => {
          // For first flight, compare with last... for any other, compare with previous
          const tripToCompare = trips.get(index - 1)

          const departure = trip.getDeparture().getIn(["where", "code"])
          const arrival = tripToCompare.getArrival().getIn(["where", "code"])

          return arrival !== departure ? acc.set(index, { arrival, departure }) : acc
        }, Map())
      : Map()
  }

  getFlightsDifferentAirports(journeyType: string = this.getType()): DifferentAirports {
    const trips = this.get("trips")

    // @ts-expect-error: Flow to TS migration.
    return trips.reduce((acc, trip, index) => {
      let newAcc = acc
      const flights = trip.get("flights")
      newAcc = newAcc.mergeDeep(
        // @ts-expect-error: Flow to TS migration.
        flights.reduce((flightsAcc, flight, i) => {
          if (i === 0 || flight.get("isThrowawayTicket") || flight.get("isHiddenCity")) {
            // Skip first flight
            return flightsAcc
          }

          const compareFlightIndex = i - 1
          const compareFlightArrival = flights.getIn([
            compareFlightIndex,
            "arrival",
            "where",
            "code",
          ])
          const flightDeparture = flight.getIn(["departure", "where", "code"]) // Current flight departure

          return flightDeparture !== compareFlightArrival
            ? flightsAcc.setIn([index, flightDeparture.toLowerCase()], {
                arrival: compareFlightArrival,
                departure: flightDeparture,
                // Fix this the next time the file is edited. (Flow to TS)
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                dataIdToHighlight: `${compareFlightIndex}-${compareFlightArrival.toLowerCase()}`,
                city: flight.getIn(["departure", "where", "name"]),
              })
            : flightsAcc
        }, Map()),
      )

      if (trips.size > 1) {
        const compareTripIndex = index === 0 ? trips.size - 1 : index - 1
        const compareTrip = trips.get(compareTripIndex)
        const departureTrip = trip.getDepartureWithoutHiddenFlights()
        const compareArrivalTrip = compareTrip.getArrivalWithoutHiddenFlights()
        if (!compareArrivalTrip || !departureTrip) {
          return newAcc
        }

        const tripDeparture = departureTrip.getIn(["where", "code"])
        const tripArrival = compareArrivalTrip.getIn(["where", "code"])
        const tripCity = departureTrip.getIn(["where", "name"])

        // Some cases highlight departure airport for multicity
        if (index === 0 && journeyType === "multicity") {
          return newAcc
        }

        if (tripDeparture !== tripArrival) {
          const tripIndex = index === 0 ? trips.size - 1 : index
          const airportKey = index === 0 ? tripArrival.toLowerCase() : tripDeparture.toLowerCase()
          const lastPart = index === 0
          newAcc = newAcc.setIn([tripIndex, airportKey], {
            arrival: tripArrival,
            departure: tripDeparture,
            dataIdToHighlight:
              index === 0
                ? // Fix this the next time the file is edited. (Flow to TS)
                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  `0-${tripDeparture.toLowerCase()}`
                : // Fix this the next time the file is edited. (Flow to TS)
                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  `${compareTripIndex}-${tripArrival.toLowerCase()}`,
            city: tripCity,
            lastPart,
          })
        }
      }

      return newAcc
    }, Map())
  }

  getType() {
    return this.get("type")
  }

  getStops(): Stop[] {
    return R.compose(
      R.flatten,
      // @ts-expect-error: Flow to TS migration.
      R.map((trip) => trip.getStops().toArray()),
    )(this.get("trips").toArray())
  }

  hasAirline(input: string) {
    return this.get("trips").reduce(
      // @ts-expect-error: Flow to TS migration.
      (result, trip) =>
        result ||
        trip.get("flights").reduce(
          // @ts-expect-error: Flow to TS migration.
          (result2, flight) => result2 || flight.get("airline").get("code") === input,
          result,
        ),
      false,
    )
  }

  hasAirport(code: string) {
    return this.getAirports().has(code)
  }

  hasBagsRecheck() {
    return this.getFlights().toJS().some(get("bagsRecheckRequired"))
  }

  hasGuarantee(): boolean {
    return this.getFlights().toJS().some(get("guarantee"))
  }

  hasPaidHandBagsAllowed() {
    // https://github.com/facebook/immutable-js/issues/989
    return this.getAirlines().equals(List.of("F9"))
  }

  hasTransfer() {
    return this.get("transfers").size > 0
  }

  hasAvailable(what: string) {
    // Possible `what`s are fields in airlineRestrictions
    // Default is true
    return R.compose(
      R.all(get([what])),
      // @ts-expect-error: Flow to TS migration.
      R.filter((airline) => this.hasAirline(airline.id)),
      R.values,
    )(airlineRestrictions)
  }

  // Don't us (handBagsAllowedForInfants, holdBagsAllowedForInfants, bagsAllowedForInfants)
  // all of these functions are incorrect
  // TODO: Get rid off them after BE infants release

  handBagsAllowedForInfants() {
    return this.hasAvailable("holdBagForInfant")
  }

  isChangeAllowed(hoursAllowedToChange: number) {
    // @ts-expect-error: Flow to TS migration.
    return this.get("trips").every((trip) => trip.isChangeAllowed(hoursAllowedToChange))
  }

  isChangeAllowedForAnyTrip(hoursAllowedToChange: number) {
    // @ts-expect-error: Flow to TS migration.
    return this.get("trips").some((trip) => trip.isChangeAllowed(hoursAllowedToChange))
  }
}
